import {
  FieldError,
  Input,
  Label,
  Text,
  TextField as AriaTextField,
  type TextFieldProps as AriaTextFieldProps,
} from 'react-aria-components';

import styles from './TextField.module.css';

export interface TextFieldProps extends Omit<
  AriaTextFieldProps,
  'children' | 'className' | 'style'
> {
  /** Always visible, above the field. Never use the placeholder as the label. */
  label: string;
  /** Helper text below the field. */
  description?: string;
  /** Unit shown inside the right edge, e.g. "mmHg", "°C", "kg". Read to screen readers with the label. */
  unit?: string;
  /** What is wrong and how to fix it. Setting it marks the field invalid. */
  errorMessage?: string;
  placeholder?: string;
  dense?: boolean;
  /**
   * Width by expected content: `xs` (8rem) for vitals and short codes, `sm` (12rem) for
   * amounts and dates, `md` (20rem) for phone numbers and names. Unset fills the form width.
   */
  width?: FieldWidth;
}

/** Field widths by expected content; unset fills the form width (720px at most). */
export type FieldWidth = 'xs' | 'sm' | 'md' | 'lg';

export function TextField({
  label,
  description,
  unit,
  errorMessage,
  placeholder,
  dense = false,
  width,
  isInvalid,
  ...rest
}: TextFieldProps) {
  return (
    <AriaTextField
      {...rest}
      isInvalid={isInvalid === true || errorMessage !== undefined}
      className={styles.field}
      data-width={width}
      data-dense={dense || undefined}
    >
      <Label className={styles.label}>
        {unit === undefined ? (
          label
        ) : (
          // One text node for the accessible name ("Temperature (°C)"); the visible label
          // stays short because the unit is shown inside the field.
          <>
            <span aria-hidden="true">{label}</span>
            <span className={styles.visuallyHidden}>{`${label} (${unit})`}</span>
          </>
        )}
      </Label>
      <div className={styles.control}>
        <Input className={styles.input} {...(placeholder === undefined ? {} : { placeholder })} />
        {unit === undefined ? null : (
          <span className={styles.unit} aria-hidden="true">
            {unit}
          </span>
        )}
      </div>
      <FieldError className={styles.error}>{errorMessage}</FieldError>
      {description === undefined ? null : (
        <Text slot="description" className={styles.description}>
          {description}
        </Text>
      )}
    </AriaTextField>
  );
}
